import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import {
  Chip,
  Colors,
  Divider,
  Flex,
  HEADER_HEIGHT,
  HorizontalSpacing,
  IconChevronDown,
  List,
  type ListRef,
  NaviIconSearch,
  useSafeArea,
  useScreenSize,
} from '@banhala/ui-kit';
import { useMarketCategories } from './hooks/useMarketCategories';
import type { MarketCategoriesPropsType } from './MarketCategoriesProps';
import type { MarketCategory } from 'shared/src/api/marketService/getMarketCategories';
import { useAtom, useAtomValue } from 'jotai';
import {
  marketGoodsCategoriesFilterAtom,
  marketGoodsListFilterAtom,
} from '../../states/GoodsListFilter.atom';
import { isAndroid, isNil } from 'shared/src/utils';
import {
  convertSortTypeLabel,
  useSelectSortType,
} from './hooks/useSelectSortType';
import { InputKeywordSearch } from './components/InputKeywordSearch';
import { GoodsCountHeader } from './components/GoodsCountHeader';
import Animated, {
  interpolate,
  useAnimatedScrollHandler,
  useAnimatedStyle,
} from 'react-native-reanimated';
import View = Animated.View;
import { ScrollContext } from '../MarketHomeBanner/context/ScrollContext';
import { useLogApplyCategoryChipFilter } from './hooks/useLogApplyCategoryChipFilter';
import { useLogClickSort } from './hooks/useLogClickSort';
import { useLogApplySort } from './hooks/useLogApplySort';
import { useLogClickMarketSearch } from './hooks/useLogClickMarketSearch';
import { SubCategories } from './components/SubCategories';
import { ErrorBoundary } from 'shared/src/component';
import { useMarketSno } from '../../hooks/useMarketSno';

const ChipSeparator = () => <HorizontalSpacing size={8} />;

const ChipHeader: React.FC<{
  marketId: number;
  showSearch: boolean;
  onPressSearch: () => void;
  onPressSort: () => void;
  onPressAll: () => void;
}> = props => {
  const value = useAtomValue(marketGoodsListFilterAtom);

  return (
    <Flex.Row gap={8} margin={{ right: 8 }}>
      <Chip
        size={'medium'}
        state={props.showSearch ? 'selected' : 'enabled'}
        leftIcon={<NaviIconSearch />}
        onPress={props.onPressSearch}
      >
        검색
      </Chip>
      <Chip
        size={'medium'}
        rightIcon={<IconChevronDown />}
        onPress={props.onPressSort}
      >
        {convertSortTypeLabel(value[props.marketId].sort)}
      </Chip>

      <Chip
        size={'medium'}
        state={
          isNil(value[props.marketId].categorySno) ? 'selected' : 'enabled'
        }
        onPress={props.onPressAll}
      >
        전체
      </Chip>
    </Flex.Row>
  );
};

const AnimatedView = Animated.createAnimatedComponent(View);
const AnimatedList = Animated.createAnimatedComponent(List<MarketCategory>);
const MarketCategories: React.FC<MarketCategoriesPropsType> = props => {
  const marketId = useMarketSno();
  const categoriesListRef = useRef<ListRef<MarketCategory>>(null);
  const { data: categories, isLoading: isCategoriesLoading } =
    useMarketCategories(marketId);

  // const [showSearch, setShowSearch] = useState(false);
  const [categoryFilter, setCategoryFilter] = useAtom(
    marketGoodsCategoriesFilterAtom,
  );
  const showSearch = useMemo(
    () => categoryFilter[marketId]?.showSearch ?? false,
    [categoryFilter, marketId],
  );

  const [value, setValue] = useAtom(marketGoodsListFilterAtom);
  const { show: showSelectSortType } = useSelectSortType();
  const { top } = useSafeArea();
  const { width: screenWidth } = useScreenSize();

  const { scrollY, categoriesScrollX } = useContext(ScrollContext);

  const logApplyCategoryChipFilter = useLogApplyCategoryChipFilter();
  const logClickSort = useLogClickSort();
  const logApplySort = useLogApplySort();
  const logClickSearch = useLogClickMarketSearch();

  // const testGroupB = useExperimentGroup({
  //   sno: 'EXP1509',
  //   targetGroups: ['B'],
  // });

  const animStyle = useAnimatedStyle(() => {
    if (!scrollY) return {};

    // const targetHeight = testGroupB ? 0 : HEADER_HEIGHT + top;
    const targetHeight = HEADER_HEIGHT + top;

    if (props.isOverlay) {
      if (props.positionY === 0)
        return {
          backgroundColor: Colors.bg_always_white,
          opacity: 0,
        };
      return {
        backgroundColor: Colors.bg_always_white,
        opacity: interpolate(
          scrollY.value,
          [
            0,
            props.positionY - targetHeight,
            props.positionY - targetHeight + 1,
          ],
          [0, 0, 1],
          'clamp',
        ),
      };
    }
    return {
      backgroundColor: Colors.bg_always_white,
      top: interpolate(
        scrollY.value,
        [0, props.positionY - targetHeight, props.positionY],
        [0, 0, targetHeight],
        'clamp',
      ),
    };
  });

  const handleSelectCategory = (item: MarketCategory) => {
    logApplyCategoryChipFilter(item);
    setValue(prev => ({
      ...prev,
      [marketId]: {
        ...prev[marketId],
        categorySno: item.sno,
        subCategorySno: item.sno,
      },
    }));
  };

  const scrollToItemTargetPosition = useMemo(() => {
    return screenWidth * 0.5 - 20;
  }, [screenWidth]);

  const [targetCategorySno, setTargetCategorySno] = useState<number | null>(
    null,
  );

  useEffect(() => {
    const categorySno = value[marketId].categorySno;
    if (targetCategorySno !== categorySno) {
      setTargetCategorySno(categorySno);
      const item =
        categories?.categories.find(cat => cat.sno === categorySno) ?? null;
      if (item) {
        categoriesListRef.current?.scrollToItem({
          item,
          animated: true,
          viewOffset: scrollToItemTargetPosition,
        });
      }
    }
  }, [
    categories?.categories,
    marketId,
    scrollToItemTargetPosition,
    targetCategorySno,
    value,
  ]);

  useEffect(() => {
    if (!categoriesScrollX) return;

    if (!isAndroid) return;

    if (props.isOverlay) {
      if (props.showCategoriesStickyView) {
        // 스크롤 하기
        categoriesListRef.current?.scrollToOffset({
          animated: false,
          offset: categoriesScrollX.value,
        });
      }
    } else {
      if (!props.showCategoriesStickyView) {
        // 스크롤하기
        categoriesListRef.current?.scrollToOffset({
          animated: false,
          offset: categoriesScrollX.value,
        });
      }
    }
  }, [categoriesScrollX, props.isOverlay, props.showCategoriesStickyView]);

  const scrollHandler = useAnimatedScrollHandler(event => {
    if (!categoriesScrollX) return;

    categoriesScrollX.value = event.contentOffset.x;
  });

  if (isCategoriesLoading) {
    return <MarketCategoriesSkeleton />;
  }

  if (isNil(categories)) {
    return null;
  }

  return (
    <AnimatedView style={animStyle}>
      <AnimatedList
        ref={categoriesListRef}
        onScroll={scrollHandler}
        onScrollToIndexFailed={() => {}}
        data={categories?.categories ?? []}
        renderItem={({ item }) => {
          return (
            <Chip
              size={'medium'}
              onPress={() => handleSelectCategory(item)}
              testID={`마켓홈카테고리필터?name=${item.name}`}
              state={
                item.sno === value[marketId].categorySno
                  ? 'selected'
                  : 'enabled'
              }
            >
              {item.name}
            </Chip>
          );
        }}
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingVertical: 12,
          zIndex: 3,
        }}
        showsHorizontalScrollIndicator={false}
        ItemSeparatorComponent={ChipSeparator}
        ListHeaderComponent={
          <ChipHeader
            marketId={marketId}
            showSearch={showSearch}
            onPressAll={() => {
              setValue(prev => ({
                ...prev,
                [marketId]: {
                  ...prev[marketId],
                  categorySno: null,
                  subCategorySno: null,
                },
              }));
            }}
            onPressSearch={() => {
              logClickSearch();
              if (showSearch) {
                setValue(prev => ({
                  ...prev,
                  [marketId]: { ...prev[marketId], keyword: '' },
                }));
              }
              setCategoryFilter(prev => ({
                ...prev,
                [marketId]: {
                  ...prev[marketId],
                  showSearch: !(prev[marketId]?.showSearch ?? false),
                },
              }));
            }}
            onPressSort={() => {
              logClickSort(value[marketId].sort);
              showSelectSortType({
                current: value[marketId].sort,
              }).then(result => {
                if (result.type === 'confirm') {
                  logApplySort(result.data.selected);
                  setValue(prev => ({
                    ...prev,
                    [marketId]: {
                      ...prev[marketId],
                      sort: result.data.selected,
                    },
                  }));
                }
              });
            }}
          />
        }
        horizontal={true}
      />
      {showSearch && (
        <InputKeywordSearch
          focusable={props.isOverlay ? props.showCategoriesStickyView : true}
          marketId={marketId}
          onPressCancel={() => {
            // setShowSearch(false);
            setCategoryFilter(prev => ({
              ...prev,
              [marketId]: {
                ...prev[marketId],
                showSearch: false,
              },
            }));
            setValue(prev => ({
              ...prev,
              [marketId]: { ...prev[marketId], keyword: '' },
            }));
          }}
        />
      )}
      <Divider size={'small'} />

      {!showSearch && <SubCategories marketId={marketId} />}

      <GoodsCountHeader marketId={marketId} />
    </AnimatedView>
  );
};

const MarketCategoriesSkeleton = () => {
  return <Flex backgroundColor={'bg_secondary'} />;
};

const MarketCategoriesWrapper: React.FC<MarketCategoriesPropsType> = props => {
  return (
    // 에러 시 카테고리 안 보여주기
    <ErrorBoundary ErrorFallback={() => <></>}>
      <MarketCategories {...props} />
    </ErrorBoundary>
  );
};

export default MarketCategoriesWrapper;
